import { FEATURE_FLAG_KEYS } from '@alpha-core/feature-flags';
import {
    BranchErrorService,
    BranchNotFoundDialogComponent,
    PackageInitializerUtil,
    PackageType,
    Status,
} from '@alpha-core/platform-ui';
import { InjectFFIntoWindow } from '@alpha-core/platform-ui/utils/inject-ff-into-window.js';
import { getPluginTimeoutMs, listenerPromise } from '@alpha-core/platform-ui/utils/listener-promise';
import { inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ResolveFn } from '@angular/router';
import { FeatureFlagsService } from '@neutrinos/ngx-feature-flags';
import { DialogService } from 'primeng/dynamicdialog';
import { BranchStateService } from '../services/branch-state.service.js';
import { CustomCodeService } from '../services/custom-code.service.js';
import { LanguagesService } from '../services/language.service.js';
import { PackageManagementService } from '../services/package-management.service.js';
import { ProjectService } from '../services/project.service.js';
import { TenantService } from '../services/tenant.service.js';

declare global {
    interface Window {
        onAlphaReadyHandler?: () => Promise<void>;
        onAlphaApLoadedHandler?: () => void;
        onPluginReadyHandler?: () => Promise<void>;
        isAlphaReady: Status;
        isPluginReady: Status;
    }
}

let pluginsRegisteredWhenProjectPluginsFeatureIsOff = false;
let alphaReadyFired = false;
let alphaApLoadedFired = false;

function tryTriggerPluginReadyHandler() {
    if (alphaReadyFired && alphaApLoadedFired && window.onPluginReadyHandler) {
        window.removeEventListener('alpha_ready', window.onAlphaReadyHandler!);
        window.removeEventListener('alpha-ap-loaded', window.onAlphaApLoadedHandler!);
        window.onPluginReadyHandler();
    }
}

export const projectResolver: ResolveFn<boolean> = async (route) => {
    const branchStateService = inject(BranchStateService);
    const projectService = inject(ProjectService);
    const languageService = inject(LanguagesService);
    const customCodeService = inject(CustomCodeService);
    const title = inject(Title);
    const tenantService = inject(TenantService);
    const packageManagementService = inject(PackageManagementService);
    const featureFlagService = inject(FeatureFlagsService);
    InjectFFIntoWindow(featureFlagService);
    const branchErrorService = inject(BranchErrorService);
    const dialogService = inject(DialogService);

    const projectId = route.params['projectId'] || route.queryParams['projectId'];

    // Branch validation is handled by branchValidationGuard before this resolver runs
    const branch = route.queryParams['branch'] || 'main';
    branchStateService.setBranch(branch);

    const project = await projectService.setProject(projectId);
    if (project) {
        languageService.fetchLanguages().subscribe();
        await languageService.fetchDefaultTranslationKeys();
        customCodeService.fetchAllCustomCode();
        title.setTitle(project.name + ' - ' + project.projectId);

        // Show branch not found dialog if redirected from invalid branch
        const invalidBranch = branchErrorService.getAndClearInvalidBranch();
        if (invalidBranch) {
            dialogService.open(BranchNotFoundDialogComponent, {
                header: `Branch "${invalidBranch}" not found. Redirecting to main branch.`,
                width: '35rem',
                closable: false,
            });
        }

        // Package initialize
        const latestTag = await projectService.getLatestProjectTag(project.projectId);
        const tenantName = tenantService.fetchTenant();

        if (!tenantName) {
            throw new Error('Tenant not found');
        }
        const util = new PackageInitializerUtil(project.projectId, tenantName, latestTag?.name);
        packageManagementService.packageInitializerUtil = util;
        if (!featureFlagService.isFeatureEnabled(FEATURE_FLAG_KEYS.PROJECT_PLUGINS)) {
            if (!pluginsRegisteredWhenProjectPluginsFeatureIsOff) {
                util.requestForBundles();
                await listenerPromise('alpha_ready', `Alpha module manager loaded`, getPluginTimeoutMs());
                await util.requestForElements();
                pluginsRegisteredWhenProjectPluginsFeatureIsOff = true;
            }
            return true;
        }
        if (window.onAlphaReadyHandler) {
            window.removeEventListener('alpha_ready', window.onAlphaReadyHandler);
        }
        if (window.onAlphaApLoadedHandler) {
            window.removeEventListener('alpha-ap-loaded', window.onAlphaApLoadedHandler);
        }

        window.onPluginReadyHandler = async () => {
            try {
                if (window.isPluginReady == Status.ERROR) throw new Error('Plugin Load Failed');
                window.isPluginReady = Status.READY;
                await util.requestForElements();
            } catch (e) {
                console.error('Error in onPluginReadyHandler:', e);
            }
        };

        window.onAlphaReadyHandler = async () => {
            try {
                window.isAlphaReady = Status.READY;
                await util.loadProjectPackages(PackageType.MODULE);
            } catch (e) {
                console.error('Error in onAlphaReadyHandler:', e);
            } finally {
                alphaReadyFired = true;
                tryTriggerPluginReadyHandler();
            }
        };

        window.onAlphaApLoadedHandler = () => {
            alphaApLoadedFired = true;
            tryTriggerPluginReadyHandler();
        };

        window.addEventListener('alpha-ap-loaded', window.onAlphaApLoadedHandler);

        if (window.isAlphaReady === Status.READY && window.alpha) {
            window.onAlphaReadyHandler();
            return true;
        }

        window.addEventListener('alpha_ready', window.onAlphaReadyHandler);

        util.loadAlphaModules();
        return true;
    }
    throw new Error('Project Not Found');
};
